/**
 * StudiosListPage - Admin list view of all studios
 */

import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  getAdminStudios,
  createAdminStudio,
  type AdminStudio,
} from '../../../api/admin';
import SortableHeader from '../../../components/admin/SortableHeader';
import { PlacesAutocomplete, type PlaceResult } from '../../../components/events/PlacesAutocomplete';
import { US_STATES } from '../../../utils/usStates';
import Pagination from '../../../components/admin/Pagination';
import { formatPhoneDisplay } from '../../../utils/phone';
import { useDebounce } from '../../../hooks/useDebounce';

type StudioStatus = 'all' | 'active' | 'inactive';

const STUDIO_STATUSES: StudioStatus[] = ['all', 'active', 'inactive'];

/**
 * SDA-281: the list opens on Active — the studios that have actually activated an
 * account. The ~10.8k never-activated import rows are still one dropdown click away
 * under "All Status"; they are the cold-outreach audience, not the working list.
 */
const DEFAULT_STATUS: StudioStatus = 'active';

const parseStatus = (raw: string | null): StudioStatus =>
  STUDIO_STATUSES.includes(raw as StudioStatus) ? (raw as StudioStatus) : DEFAULT_STATUS;


export default function StudiosListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [studios, setStudios] = useState<AdminStudio[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // Distinguishes the first load (full skeleton) from refetches, where the
  // previous rows stay mounted under a spinner overlay (SDA-264, same pattern
  // as BookingsListPage/InquiriesListPage).
  const [hasLoaded, setHasLoaded] = useState(false);
  // Set when the search matched too many contacts for the backend to fold into
  // one filter, so the list below may be missing studios (SDA-264).
  const [searchTruncated, setSearchTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<StudioStatus>(
    parseStatus(searchParams.get('status'))
  );
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [hasBookings, setHasBookings] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  // SDA-264: debounce the free-text search so we fetch once the user pauses
  // typing, not on every keystroke — per-keystroke fetches also raced each
  // other, letting a slow stale response overwrite newer results.
  const debouncedSearch = useDebounce(searchQuery, 300);
  // Guards against out-of-order responses (latest request wins), same as
  // FacultyListPage — the debounce narrows the race but doesn't close it.
  const reqIdRef = useRef(0);

  // Sort
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const navigate = useNavigate();

  // Create Studio modal (SDA-140)
  const [showCreate, setShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    address_line1: '',
    city: '',
    state: '',
    google_place_id: '',
    zip_code: '',
    phone: '',
    website: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
    contact_phone: '',
    send_invite_email: true,
  });

  const resetCreateForm = () => {
    setCreateForm({
      name: '', address_line1: '', city: '', state: '', google_place_id: '', zip_code: '',
      phone: '', website: '', contact_first_name: '', contact_last_name: '',
      contact_email: '', contact_phone: '', send_invite_email: true,
    });
    setCreateError(null);
  };

  const handleCreateStudio = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreateError(null);
    if (!createForm.name.trim() || !createForm.zip_code.trim()) {
      setCreateError('Studio name and ZIP code are required.');
      return;
    }
    // If any contact field is filled, require the core three.
    const wantsContact = createForm.contact_first_name || createForm.contact_last_name || createForm.contact_email;
    if (wantsContact && (!createForm.contact_first_name || !createForm.contact_last_name || !createForm.contact_email)) {
      setCreateError('To add a primary contact, fill first name, last name, and email.');
      return;
    }
    // When sending the invite email, a primary contact email is required.
    if (createForm.send_invite_email && !createForm.contact_email) {
      setCreateError(
        'Primary contact email is required to send the invite email. Add a contact, or check "Don\'t send invite email" to create silently.',
      );
      return;
    }
    try {
      setIsCreating(true);
      const { studio, email_sent } = await createAdminStudio({
        name: createForm.name.trim(),
        address_line1: createForm.address_line1 || null,
        city: createForm.city || null,
        state: createForm.state || null,
        google_place_id: createForm.google_place_id || undefined,
        zip_code: createForm.zip_code.trim(),
        phone: createForm.phone || null,
        website: createForm.website || null,
        primary_contact: wantsContact
          ? {
              first_name: createForm.contact_first_name,
              last_name: createForm.contact_last_name,
              email: createForm.contact_email,
              phone: createForm.contact_phone || null,
            }
          : undefined,
        send_invite_email: createForm.send_invite_email,
      });
      // Warn the admin if they expected the invite to go out but it didn't.
      if (createForm.send_invite_email && !email_sent) {
        window.alert(
          'Studio created, but the invite email could not be sent (no system email account connected or send failed). You can resend it manually.',
        );
      }
      setShowCreate(false);
      resetCreateForm();
      navigate(`/admin/studios/${studio.id}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create studio');
    } finally {
      setIsCreating(false);
    }
  };

  // Stats (used internally but not displayed as cards)
  const [, setStats] = useState({
    total_studios: 0,
    active_studios: 0,
    verified_studios: 0,
    with_bookings: 0,
  });

  useEffect(() => {
    loadStudios();
    // Update URL params
    const params = new URLSearchParams();
    params.set('status', statusFilter);
    if (stateFilter) params.set('state', stateFilter);
    if (debouncedSearch) params.set('q', debouncedSearch);
    setSearchParams(params);
  }, [statusFilter, stateFilter, debouncedSearch, hasBookings, sortBy, sortOrder, page]);

  const loadStudios = async () => {
    const reqId = ++reqIdRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const { studios: studiosData, total: totalCount, search_truncated, stats: statsData } = await getAdminStudios({
        is_active: statusFilter === 'active' ? 'true' : statusFilter === 'inactive' ? 'false' : undefined,
        state: stateFilter || undefined,
        search: debouncedSearch || undefined,
        has_bookings: hasBookings ? 'true' : undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        limit,
      });
      // Ignore stale responses — only the latest request may update state.
      if (reqId !== reqIdRef.current) return;
      setStudios(studiosData);
      setTotal(totalCount);
      setSearchTruncated(search_truncated === true);
      if (statsData) {
        setStats(statsData);
      }
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load studios');
    } finally {
      if (reqId === reqIdRef.current) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const statusBadge = (isActive: boolean) => (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-success-100 text-success-700' : 'bg-warm-200 text-warm-600'}`}>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );

  const totalPages = Math.ceil(total / limit);

  if (isLoading && !hasLoaded) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
          <div className="h-10 bg-warm-200 rounded w-32 animate-pulse" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-xl p-3 sm:p-4 animate-pulse">
              <div className="h-8 bg-warm-100 rounded w-1/2 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-3/4" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="h-5 bg-warm-100 rounded w-1/3 mb-2" />
                  <div className="h-4 bg-warm-100 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Studios</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Manage dance studios and their bookings.
          </p>
        </div>
        <button
          onClick={() => { resetCreateForm(); setShowCreate(true); }}
          className="btn-primary whitespace-nowrap"
        >
          + Create Studio
        </button>
      </div>


      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col gap-3">
          {/* Search - full width on mobile */}
          <div className="w-full">
            <label className="block text-sm font-medium text-warm-700 mb-1">
              Search
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                placeholder="Search studios, contacts, cities..."
                className="w-full px-3 py-2.5 pr-9 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {isLoading && hasLoaded && (
                <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Searching">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            {/* Status Filter */}
            <div className="w-full sm:min-w-[150px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as StudioStatus);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* State Filter */}
            <div className="w-full sm:min-w-[150px] sm:w-auto">
              <label className="block text-sm font-medium text-warm-700 mb-1">
                State
              </label>
              <select
                value={stateFilter}
                onChange={(e) => {
                  setStateFilter(e.target.value);
                  setPage(1);
                }}
                className="w-full px-3 py-2.5 border border-warm-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All States</option>
                {US_STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>

            {/* With Bookings Filter */}
            <label className="flex items-center gap-2 text-sm text-warm-700 cursor-pointer min-h-[44px]">
              <input
                type="checkbox"
                checked={hasBookings}
                onChange={(e) => { setHasBookings(e.target.checked); setPage(1); }}
                className="w-4 h-4 rounded border-warm-300 text-primary-600 focus:ring-primary-500"
              />
              With Bookings Only
            </label>

            {/* Clear Filters */}
            {(statusFilter !== DEFAULT_STATUS || stateFilter || searchQuery || hasBookings) && (
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setStatusFilter(DEFAULT_STATUS);
                    setStateFilter('');
                    setSearchQuery('');
                    setHasBookings(false);
                    setPage(1);
                  }}
                  className="px-4 py-2.5 min-h-[44px] text-sm text-warm-600 hover:text-warm-900 hover:bg-warm-100 rounded-lg transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* During refetch the previous rows stay mounted, dimmed under a spinner overlay,
          so a search or filter change never blanks the list and repaints (same pattern
          as BookingsListPage). The partial-results banner sits inside so it dims too
          rather than asserting a stale claim about results still being fetched. */}
      <div className="relative">
        {isLoading && hasLoaded && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 rounded-xl">
            <svg className="w-8 h-8 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Loading studios">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
        )}

      {searchTruncated && (
        <div className="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg text-warning-700">
          Some matching studios may be missing — your search matches too many contacts to check them all. Keep typing to narrow it.
        </div>
      )}

      {/* Studios List */}
      {studios.length === 0 && !isLoading ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">No studios found</h3>
          <p className="text-warm-600 mb-6">
            {searchQuery || statusFilter !== DEFAULT_STATUS || stateFilter || hasBookings
              ? 'Try adjusting your filters to see more results.'
              : 'No studios have been registered yet.'}
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm-50 border-b border-warm-100">
                <tr>
                  <SortableHeader label="Studio" field="name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Primary Contact
                  </th>
                  <SortableHeader label="Location" field="city" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <SortableHeader label="Bookings" field="total_bookings" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                  <th className="text-left px-6 py-4 text-sm font-semibold text-warm-700">
                    Status
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {studios.map((studio) => (
                  <tr key={studio.id} className="hover:bg-warm-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <Link
                          to={`/admin/studios/${studio.id}`}
                          className="font-medium text-warm-900 hover:text-primary-600 transition-colors"
                        >
                          {studio.name}
                        </Link>
                        {(studio.city || studio.state) && (
                          <p className="text-sm text-warm-500">
                            {[studio.city, studio.state, studio.zip_code].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {studio.primary_contact ? (
                        <div>
                          <p className="text-sm text-warm-900">
                            {studio.primary_contact.first_name} {studio.primary_contact.last_name}
                          </p>
                          <p className="text-sm text-warm-500">{studio.primary_contact.email}</p>
                        </div>
                      ) : (
                        <span className="text-warm-400 text-sm">--</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm text-warm-700">
                        {studio.city && studio.state
                          ? `${studio.city}, ${studio.state}`
                          : studio.city || studio.state || '--'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {studio.total_bookings > 0 ? (
                        <Link to={`/admin/bookings?studio_id=${studio.id}`} className="text-sm font-medium text-primary-600 hover:text-primary-700 hover:underline">
                          {studio.total_bookings}
                        </Link>
                      ) : (
                        <span className="text-sm text-warm-400">0</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        {/* SDA-281: Active = the studio activated an account (not is_active, which is always true). */}
                        {statusBadge(!studio.is_dormant)}
                        {studio.is_verified && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                            Verified
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/admin/studios/${studio.id}`}
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {studios.map((studio) => (
              <Link
                to={`/admin/studios/${studio.id}`}
                key={studio.id}
                className="block bg-white rounded-xl border border-warm-200 p-4 active:scale-[0.98] transition-transform hover:border-warm-300"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-warm-900 truncate">{studio.name}</p>
                  <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                    {/* SDA-281: Active = the studio activated an account (not is_active, which is always true). */}
                    {statusBadge(!studio.is_dormant)}
                    {studio.is_verified && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-700">
                        Verified
                      </span>
                    )}
                  </div>
                </div>
                {(studio.city || studio.state || studio.zip_code) && (
                  <p className="text-sm text-warm-500 mb-2">
                    {[studio.city, studio.state, studio.zip_code].filter(Boolean).join(', ')}
                  </p>
                )}
                {studio.primary_contact && (
                  <p className="text-sm text-warm-500 mb-2">
                    {studio.primary_contact.first_name} {studio.primary_contact.last_name} &middot; {studio.primary_contact.email}
                  </p>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-warm-600">
                    {studio.total_bookings} booking{studio.total_bookings !== 1 ? 's' : ''}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              page={page}
              total={total}
              limit={limit}
              itemLabel="studios"
              onPageChange={setPage}
            />
          )}
        </>
      )}
      </div>

      {/* Create Studio Modal (SDA-140) */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleCreateStudio} className="p-6">
              <h2 className="text-xl font-semibold text-warm-900 mb-2">Create Studio</h2>
              <p className="text-sm text-warm-500 mb-4">
                {createForm.send_invite_email
                  ? 'An invite email will be sent to the primary contact so they can claim the studio.'
                  : 'Studio will be created silently — no email sent. The contact can claim it later when they register with the contact email.'}
              </p>

              {createError && (
                <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-sm text-danger-700">
                  {createError}
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Studio Name *</label>
                  <input
                    type="text" value={createForm.name}
                    onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Ryan Dance Studio" required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-warm-700 mb-1">Address</label>
                  <input
                    type="text" value={createForm.address_line1}
                    onChange={(e) => setCreateForm(f => ({ ...f, address_line1: e.target.value }))}
                    className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    placeholder="123 Main St"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-warm-700 mb-1">City</label>
                    <PlacesAutocomplete
                      mode="city"
                      value={createForm.city}
                      onInputChange={(textVal) => setCreateForm(f => ({ ...f, city: textVal, state: '', google_place_id: '' }))}
                      onPlaceSelected={(place: PlaceResult) => {
                        const cityName = place.addressComponents?.city || place.name;
                        setCreateForm(f => ({
                          ...f,
                          city: cityName || f.city,
                          state: place.addressComponents?.state || f.state,
                          google_place_id: place.placeId || '',
                        }));
                      }}
                      placeholder="Start typing a city…"
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-warm-700 mb-1">State</label>
                    <select
                      value={createForm.state}
                      onChange={(e) => setCreateForm(f => ({ ...f, state: e.target.value, google_place_id: '' }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="">—</option>
                      {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1">
                    <label className="block text-sm font-medium text-warm-700 mb-1">ZIP *</label>
                    <input
                      type="text" value={createForm.zip_code}
                      onChange={(e) => setCreateForm(f => ({ ...f, zip_code: e.target.value }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">Phone</label>
                    <input
                      type="tel" value={createForm.phone}
                      onChange={(e) => setCreateForm(f => ({ ...f, phone: formatPhoneDisplay(e.target.value) }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-warm-700 mb-1">Website</label>
                    <input
                      type="text" value={createForm.website}
                      onChange={(e) => setCreateForm(f => ({ ...f, website: e.target.value }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="pt-3 mt-1 border-t border-warm-200">
                  <p className="text-sm font-medium text-warm-700 mb-2">Primary Contact (optional)</p>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text" value={createForm.contact_first_name}
                      onChange={(e) => setCreateForm(f => ({ ...f, contact_first_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="First name"
                    />
                    <input
                      type="text" value={createForm.contact_last_name}
                      onChange={(e) => setCreateForm(f => ({ ...f, contact_last_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Last name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <input
                      type="email" value={createForm.contact_email}
                      onChange={(e) => setCreateForm(f => ({ ...f, contact_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="Email"
                    />
                    <input
                      type="tel" value={createForm.contact_phone}
                      onChange={(e) => setCreateForm(f => ({ ...f, contact_phone: formatPhoneDisplay(e.target.value) }))}
                      className="w-full px-3 py-2 border border-warm-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <label className="flex items-center gap-2 mt-3 text-sm text-warm-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!createForm.send_invite_email}
                      onChange={(e) => setCreateForm(f => ({ ...f, send_invite_email: !e.target.checked }))}
                      className="rounded border-warm-300 text-primary-600 focus:ring-primary-500"
                    />
                    Don't send invite email (create as silent dormant)
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowCreate(false); resetCreateForm(); }}
                  className="px-4 py-2 text-warm-600 hover:text-warm-900 transition-colors"
                  disabled={isCreating}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isCreating}>
                  {isCreating ? 'Creating...' : 'Create Studio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
